import { inject, Injectable } from '@angular/core';
import KcAdminClient from '@keycloak/keycloak-admin-client';
import { APP_ENVIRONMENT_KEYCLOAK } from '@wheere-front/shared-util';
import { KeycloakService } from 'keycloak-angular';
import OrganizationRepresentation from '@keycloak/keycloak-admin-client/lib/defs/organizationRepresentation';

@Injectable({ providedIn: 'root' })
export class KeycloakAdminService {
  keycloakEnv = inject(APP_ENVIRONMENT_KEYCLOAK);
  keycloakService = inject(KeycloakService);

  client!: KcAdminClient;

  async init(keycloakService: KeycloakService) {
    this.client = new KcAdminClient({
      baseUrl: this.keycloakEnv.issuer,
      realmName: this.keycloakEnv.realm,
    });

    const token = await keycloakService.getToken();
    this.client.setAccessToken(token);
  }

  /**
   * Refresh the admin client token when it expires
   */
  async refreshToken(): Promise<void> {
    // Force token refresh from Keycloak service
    await this.keycloakService.updateToken(-1);

    // Get the fresh token and update admin client
    const token = await this.keycloakService.getToken();
    this.client.setAccessToken(token);
  }

  /**
   * Execute admin operation with automatic token refresh on auth failure
   */
  async executeWithTokenRefresh<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error: unknown) {
      // Check if error is related to authentication (401, 403, or token expired)
      let isAuthError = false;

      if (error && typeof error === 'object') {
        // Check for HTTP response status in various possible locations
        const errorObj = error as any;

        // Common locations for HTTP status in different error formats
        const statusChecks = [
          errorObj.response?.status,
          errorObj.status,
          errorObj.statusCode,
          errorObj.code,
          errorObj.response?.data?.status,
          errorObj.error?.status,
        ];

        for (const status of statusChecks) {
          if (status === 401 || status === 403) {
            isAuthError = true;
            break;
          }
        }

        // Check error message in various locations
        if (!isAuthError) {
          const messageChecks = [
            errorObj.message,
            errorObj.error?.message,
            errorObj.response?.data?.message,
            errorObj.response?.statusText,
            String(errorObj),
          ];

          for (const message of messageChecks) {
            if (
              typeof message === 'string' &&
              (message.includes('401') ||
                message.includes('403') ||
                message.includes('unauthorized') ||
                message.includes('token') ||
                message.includes('Unauthorized') ||
                message.includes('Forbidden'))
            ) {
              isAuthError = true;
              break;
            }
          }
        }
      }

      if (isAuthError) {
        // Refresh token and retry
        await this.refreshToken();
        return await operation();
      }
      // Re-throw non-auth errors
      throw error;
    }
  }

  async getUserOrganizations(userId: string): Promise<OrganizationRepresentation[]> {
    // grab an access token from the admin client
    const token = await this.client.getAccessToken();

    const url = `${this.keycloakEnv.issuer}/admin/realms/${this.keycloakEnv.realm}/organizations/members/${userId}/organizations?briefRepresentation=false`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Organizations fetch failed: ${res.status} ${text}`);
    }
    return res.json();
  }
}
